<template>
    <!-- Already removed: nothing to offer -->
    <ui-card v-if="removal && removal.code !== 'deleted'" class="removal-card">
        <h4 class="title is-5 mb-4"><i class="fad fa-fw fa-trash-can mr-2 has-text-danger"></i>Remove {{ noun }}</h4>
        <p class="mb-0" v-if="removal.eligible && removal.po_pending">Registered by purchase order and would rather pay another way? Remove this registration, then register again and pay by credit card. Any activity on this registration will be lost.</p>
        <p class="mb-0" v-else-if="removal.eligible && removal.on_payment_plan">No longer need this {{ kind }}? You can remove it from your account. All of its activity will be lost, and your payment plan will <strong>not</strong> be cancelled or refunded.</p>
        <p class="mb-0" v-else-if="removal.eligible">No longer need this {{ kind }}? You can remove it from your account. All of its activity will be lost and any payment will not be refunded.</p>
        <div class="not-eligible" v-else><i class="far fa-circle-info mr-2"></i>{{ removal.reason }}</div>

        <template #footer v-if="removal.eligible">
            <div class="is-flex is-justify-content-flex-end">
                <b-button @click="open" type="is-danger" size="is-small" outlined><i class="fas fa-trash-can mr-2"></i>Remove {{ noun }}</b-button>
            </div>
        </template>

        <b-modal v-model="confirming" :width="600" scroll="keep" :can-cancel="!submitting">
            <ui-card class="confirm-card">
                <h4 class="title is-5 mb-4"><i class="fas fa-triangle-exclamation has-text-danger mr-2"></i>Remove {{ noun }}?</h4>
                <div class="has-text-weight-bold mb-4">{{ name }}</div>

                <p class="mb-3">Removing this {{ kind }} takes it off your account. You will lose:</p>
                <ul class="loss-list">
                    <li v-for="(line, i) in losses" :key="i">{{ line }}</li>
                </ul>

                <!-- Two acknowledgements (Derek 2026-10-19: make them slow down twice):
                     what is lost / not refunded, then that it's permanent.
                     Same toggle-row pattern as Reallocate Hours. -->
                <div class="irreversible mt-5">
                    <div class="term-row">
                        <div class="term-switch"><b-switch type="is-danger" v-model="acknowledged"/></div>
                        <div class="term-text" v-if="removal.po_pending">
                            <strong>Your purchase order will be cancelled.</strong> No payment has been collected for this registration; to attend, you will need to register again. I understand that this {{ kind }} and all of its activity will be removed.
                        </div>
                        <div class="term-text" v-else-if="removal.on_payment_plan">
                            <strong>Your payment plan is not cancelled and nothing is refunded.</strong> Removing this {{ kind }} does not cancel, reduce or pause your payment plan with The Payment Option (TPO); every remaining payment is still due. I understand that this {{ kind }} and all of its activity will be removed.
                        </div>
                        <div class="term-text" v-else>
                            <strong>No refund will be issued.</strong> {{ paymentNote || 'Any payment made for this ' + kind + ' is not refunded.' }} I understand that this {{ kind }} and all of its activity will be removed.
                        </div>
                    </div>
                    <div class="term-row is-separated">
                        <div class="term-switch"><b-switch type="is-danger" v-model="permanent"/></div>
                        <div class="term-text"><strong>I understand this is a permanent action and cannot be undone.</strong></div>
                    </div>
                </div>

                <b-message type="is-danger" v-if="submitError" class="mt-3">{{ submitError }}</b-message>

                <template #footer>
                    <div class="is-flex is-justify-content-space-between is-align-items-flex-start" style="gap: 8px">
                        <b-button @click="confirming = false" :disabled="submitting" size="is-small" type="is-primary" outlined><i class="fas fa-arrow-left mr-2"></i>Keep My {{ noun }}</b-button>
                        <b-button type="is-danger" size="is-small" :disabled="!acknowledged || !permanent" :loading="submitting" @click="submit"><i class="fas fa-trash-can mr-2"></i>Remove {{ noun }}</b-button>
                    </div>
                </template>
            </ui-card>
        </b-modal>
    </ui-card>
</template>

<script>
    import UiCard from "./UiCard.vue";
    import {client as http} from "../http_client";

    const plural = (n, word, many = word + 's') => `${n} ${n === 1 ? word : many}`;

    /**
     * "Remove Enrollment" / "Remove Registration" card for the Your
     * Enrollment / Your Registration pages. Loads eligibility from
     * `${endpoint}` (GET) and removes with POST {acknowledged, permanent};
     * emits `removed` so the page can refresh the store and navigate away.
     */
    export default {
        name: "RemovalCard",
        components: {UiCard},
        props: {
            kind: {type: String, required: true},       // 'enrollment' | 'registration'
            endpoint: {type: String, required: true},   // e.g. app/enrollments/123/removal
            name: {type: String, default: ''},
            paymentNote: {type: String, default: ''}
        },
        data() {
            return {
                removal: null,
                confirming: false,
                acknowledged: false,
                permanent: false,
                submitting: false,
                submitError: null
            };
        },
        computed: {
            noun() {
                return this.kind.charAt(0).toUpperCase() + this.kind.slice(1);
            },
            losses() {
                const r = this.removal || {};
                const lines = [];
                if (this.kind === 'enrollment') {
                    lines.push('Access to the course and all of its content');
                    if (r.log_entries) lines.push(`Your study log: ${plural(r.log_entries, 'entry', 'entries')}${r.logged_hours > 0 ? ` (${r.logged_hours} hours)` : ''}`);
                    if (r.self_directed) lines.push(r.milestones_submitted ? `Your study plan and ${plural(r.milestones_submitted, 'submitted milestone reflection')}` : 'Your study plan');
                    if (r.files || r.notes) lines.push(`Your ${[r.files ? plural(r.files, 'uploaded file') : null, r.notes ? plural(r.notes, 'note') : null].filter(Boolean).join(' and ')}`);
                } else {
                    lines.push('Access to the conference and its session content');
                    if (r.sessions) lines.push(`${plural(r.sessions, 'session')} on your schedule`);
                    if (r.log_entries) lines.push(`Your attendance log: ${plural(r.log_entries, 'entry', 'entries')}${r.logged_hours > 0 ? ` (${r.logged_hours} hours)` : ''}`);
                }
                if (r.applications_in_progress) lines.push(`${plural(r.applications_in_progress, 'certification application')} in progress`);
                return lines;
            }
        },
        watch: {
            endpoint() {
                this.load();
            }
        },
        methods: {
            load() {
                this.removal = null;
                http.get(this.endpoint, {force: true}).then(response => {
                    this.removal = response.data.removal;
                }).catch(() => {
                    // The card simply doesn't show if eligibility can't be loaded
                    this.removal = null;
                });
            },
            open() {
                this.acknowledged = false;
                this.permanent = false;
                this.submitError = null;
                this.confirming = true;
            },
            submit() {
                if (!this.acknowledged || !this.permanent || this.submitting) return;
                this.submitting = true;
                this.submitError = null;
                http.post(this.endpoint, {acknowledged: true, permanent: true}, {force: true}).then(() => {
                    this.submitting = false;
                    this.confirming = false;
                    this.$buefy.toast.open({message: `${this.noun} removed.`, type: 'is-success'});
                    this.$emit('removed');
                }).catch(err => {
                    this.submitting = false;
                    this.submitError = (err.response && err.response.data && err.response.data.error) || `The ${this.kind} could not be removed. Nothing was changed.`;
                });
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<style lang="scss" scoped>
    .not-eligible {
        color: #666d7a;
        font-size: 0.95rem;
        line-height: 1.5;
    }

    .loss-list li + li {
        margin-top: 0.25rem;
    }

    .loss-list {
        margin: 0 0 0 1.2rem;
        list-style: disc;
        font-size: 0.95rem;
    }

    .irreversible {
        background: #fdf1f0;
        border: 1px solid #f1c7c4;
        border-radius: 6px;
        padding: 0.9rem 1rem;
        color: #6b1f1a;
        font-size: 0.9rem;

        .term-row {
            display: flex;
            align-items: flex-start;
            gap: 12px;

            .term-switch {
                flex-shrink: 0;
                padding-top: 2px;
            }

            .term-text {
                flex: 1;
                min-width: 0;
            }

            &.is-separated {
                margin-top: 0.9rem;
                padding-top: 0.9rem;
                border-top: 1px solid #f1c7c4;
            }
        }
    }
</style>
